/**
 * Extract page context from the current URL to determine what resource
 * the user is viewing in Active Steward.
 */

// Modern REST-shaped patterns applied against the URL pathname (/resources/<id>).
const RESOURCE_PATTERNS = [
  { pattern: /\/constituents\/(\d+)/, resourceType: 'constituents' },
  { pattern: /\/products\/(\d+)/, resourceType: 'products' },
  { pattern: /\/studies\/(\d+)/, resourceType: 'studies' },
  { pattern: /\/entities\/(\d+)/, resourceType: 'entities' },
  { pattern: /\/documents\/(\d+)/, resourceType: 'documents' },
  { pattern: /\/contacts\/(\d+)/, resourceType: 'contacts' },
  { pattern: /\/registrations\/(\d+)/, resourceType: 'registrations' },
  { pattern: /\/submissions\/(\d+)/, resourceType: 'submissions' },
  { pattern: /\/budgets\/(\d+)/, resourceType: 'budgets' },
  { pattern: /\/milestones\/(\d+)/, resourceType: 'milestones' },
  { pattern: /\/folders\/(\d+)/, resourceType: 'folders' },
  { pattern: /\/study-groups\/(\d+)/, resourceType: 'study-groups' },
  { pattern: /\/addresses\/(\d+)/, resourceType: 'addresses' },
  // List views (no ID)
  { pattern: /\/constituents\/?$/, resourceType: 'constituents', listView: true },
  { pattern: /\/products\/?$/, resourceType: 'products', listView: true },
  { pattern: /\/studies\/?$/, resourceType: 'studies', listView: true },
  { pattern: /\/entities\/?$/, resourceType: 'entities', listView: true },
  { pattern: /\/documents\/?$/, resourceType: 'documents', listView: true },
  { pattern: /\/contacts\/?$/, resourceType: 'contacts', listView: true },
  { pattern: /\/registrations\/?$/, resourceType: 'registrations', listView: true },
  { pattern: /\/submissions\/?$/, resourceType: 'submissions', listView: true },
  { pattern: /\/budgets\/?$/, resourceType: 'budgets', listView: true },
  { pattern: /\/milestones\/?$/, resourceType: 'milestones', listView: true },
  { pattern: /\/folders\/?$/, resourceType: 'folders', listView: true },
  { pattern: /\/study-groups\/?$/, resourceType: 'study-groups', listView: true },
  { pattern: /\/addresses\/?$/, resourceType: 'addresses', listView: true },
];

// Legacy Active Steward URL shape: /ActiveSteward/<singular>/display?id=<id>. The resource id is
// in the query string, so these are matched against the full URL (path + search), and we map the
// singular path segment to the plural resource group name used elsewhere in the system.
const LEGACY_AS_PATH_TO_RESOURCE = {
  constituent: 'constituents',
  product: 'products',
  study: 'studies',
  entity: 'entities',
  document: 'documents',
  contact: 'contacts',
  registration: 'registrations',
  submission: 'submissions',
  budget: 'budgets',
  milestone: 'milestones',
  folder: 'folders',
  'study-group': 'study-groups',
  address: 'addresses',
};

const LEGACY_AS_DISPLAY = /\/([a-z-]+)\/display(?:\/[^?]*)?(?:\?|$)/i;

const extractLegacyContext = (urlOrPath) => {
  const displayMatch = urlOrPath.match(LEGACY_AS_DISPLAY);
  if (!displayMatch) return null;
  const resourceType = LEGACY_AS_PATH_TO_RESOURCE[displayMatch[1].toLowerCase()];
  if (!resourceType) return null;
  // Pull id out of the query string.
  const queryMatch = urlOrPath.match(/[?&]id=(\d+)/);
  if (queryMatch) return { resourceType, resourceId: queryMatch[1] };
  // No id → it's a list view of this resource.
  return { resourceType, listView: true };
};

export const extractPageContext = (url) => {
  if (!url) return null;

  try {
    const parsed = new URL(url);
    const pathname = parsed.pathname;
    const pathAndSearch = pathname + parsed.search;

    for (const { pattern, resourceType, listView } of RESOURCE_PATTERNS) {
      const match = pathname.match(pattern);
      if (match) {
        if (listView) return { resourceType, listView: true };
        return { resourceType, resourceId: match[1] };
      }
    }

    const legacy = extractLegacyContext(pathAndSearch);
    if (legacy) return legacy;
  } catch (e) {
    // URL parsing failed — fall back to raw string matching.
    for (const { pattern, resourceType, listView } of RESOURCE_PATTERNS) {
      const match = url.match(pattern);
      if (match) {
        if (listView) return { resourceType, listView: true };
        return { resourceType, resourceId: match[1] };
      }
    }
    const legacy = extractLegacyContext(url);
    if (legacy) return legacy;
  }

  return null;
};

export const extractPartitionId = () => {
  // 1. Active Steward body hook — reliable on every AS page, set by the master layout.
  const bodyId = document.body?.dataset?.partitionId;
  if (bodyId) {
    const parsed = parseInt(bodyId, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  // 2. URL params (some pages include ?partitionId= or ?partition=).
  try {
    const params = new URLSearchParams(window.location.search);
    const partitionId = params.get('partitionId') || params.get('partition');
    if (partitionId) return parseInt(partitionId, 10);
  } catch (e) { /* ignore */ }

  // 3. Any element with data-partition-id (fallback for embedded hooks).
  const el = document.querySelector('[data-partition-id]');
  if (el) {
    const id = el.getAttribute('data-partition-id');
    if (id) return parseInt(id, 10);
  }

  // 4. AS breadcrumb partition switcher — match the current partition name against the
  //    menu links and extract id from the link's href. Only rendered when breadcrumbs exist.
  try {
    const selectedEl = document.querySelector('.partition-select.crumb');
    if (selectedEl) {
      const selectedName = selectedEl.textContent.replace(/^\s+|\s+$/g, '');
      const partitionLinks = document.querySelectorAll('.partition-switch .partition-item a');
      for (const link of partitionLinks) {
        const linkName = link.textContent.replace(/^\s+|\s+$/g, '');
        if (linkName === selectedName) {
          const match = link.getAttribute('href')?.match(/[?&]id=(\d+)/);
          if (match) return parseInt(match[1], 10);
        }
      }
    }
  } catch (e) { /* ignore */ }

  return null;
};
